<script setup>
import { computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { Utils } from "@/utils.js";
import { useMainStore } from "@/stores/store.js";
import Button from "@/components/utils/Button.vue";
import { storeToRefs } from "pinia";

const router = useRouter();
const route = useRoute();
const store = useMainStore();

// Don't set loading to true on mount - this was causing buttons to be disabled
// store.overlays.loading = true;

const handlePrevButtonClick = () => {
	const prevStepName = Utils.getPrevStepName(store, store.user.currentStepName);
	store.gotoStep(prevStepName, router, "prev", "click");
};

const handleNextButtonClick = async () => {
	$log.log(`[AppFooter:handleNextButtonClick] Clicked`);
	if (store.stepReport) {
		$log.log(`[AppFooter:handleNextButtonClick] Triggering report generation`);
		store.triggerDocGen = true;
	} else {
		const nextStepName = await Utils.getNextStepName(
			store,
			store.user.currentStepName,
		);
		$log.log(`[AppFooter:handleNextButtonClick] nextStepName: ${JSON.stringify(nextStepName, null, 4)}`);
		store.gotoStep(nextStepName, router, "next", "click");
	}
};

const handleStartAgainClick = () => {
	store.resetUserData(false);
	store.gotoStep("Start", router, "previous", "click");
};

const getNextButtonName = () => {
	if (store.user.currentStepName === "Report") {
		return "Generate Report";
	} else {
		return "Next";
	}
};

//
// Computed properties (because they are being referenced in the code part of the component)
//
const isTargetMet = computed(() => store.isTargetMet);
const isReportValid = computed(() => store.stepReportIsValid);

// Computed class for the Next/Generate Report button
// Shows red background on Report step when report is not valid or target not met
const nextButtonClass = computed(() => {
	const baseClass = "lux-btn-small whitespace-nowrap";
	if (store.stepReport && (!isReportValid.value || !isTargetMet.value)) {
		return `${baseClass} bg-red-300 text-black`;
	}
	return `${baseClass} lux-btn-accent`;
});
</script>

<template>
	<footer
		id="luxxbox-acoustic-analyzer-footer"
		class="lux-px flex w-full items-stretch justify-items-center gap-2 py-medium text-center align-middle tracking-widest"
		v-if="!store.user.reportGenerated"
	>
		<div class="" v-if="store.user.currentStepName !== 'Start'">
			<Button
				label="Previous"
				type="button"
				buttonClass="lux-btn-white lux-btn-small"
				:disabled="
					store.user.currentStepIndex === 0 ||
					store.triggerDocGen === true ||
					store.showRoomForm === true ||
					store.overlays.loading === true
				"
				@click="handlePrevButtonClick"
			/>
		</div>
		<div class="" v-if="store.user.currentStepName !== 'Start'">
			<Button
				label="Start Again"
				type="button"
				buttonClass="lux-btn-bare-white lux-btn-small whitespace-nowrap hidden md:block"
				:disabled="
					store.user.currentStepIndex === 0 ||
					store.triggerDocGen === true ||
					store.showRoomForm === true ||
					store.overlays.loading === true
				"
				@click="handleStartAgainClick"
			/>
		</div>
		<div class="grow"></div>
		<div class="">
			<Button
				id="next_button"
				:label="getNextButtonName()"
				type="button"
				:buttonClass="nextButtonClass"
				:disabled="
					store.triggerDocGen === true ||
					store.showRoomForm === true ||
					store.overlays.loading === true ||
					(store.stepReport && !store.validateProductSize) ||
					(store.stepReport && !isTargetMet) ||
					(store.stepReport && !isReportValid)
				"
				:class="{ 'md:hidden': store.stepReport }"
				@click="handleNextButtonClick"
			/>
		</div>

		<div class="lux-pos-absmiddle hidden bg-whitea75 p-3 shadow-sm">
			<pre
				class="text-red-500"
				v-if="!store.validation[store.user.currentStepName].valid"
				>{{ store.user.currentStepName }} valid : {{
					store.validation[store.user.currentStepName].valid
				}}</pre
			>
			<pre
				class="text-green-500"
				v-if="store.validation[store.user.currentStepName].valid"
				>{{ store.user.currentStepName }} valid : {{
					store.validation[store.user.currentStepName].valid
				}}</pre
			>
		</div>

		<div
			class="lux-fixed-absmiddle flex flex-col items-center rounded-md bg-black p-8 text-center shadow-sm"
			v-if="store.overlays.loading"
		>
			<div class="loader"></div>
			<div class="mt-3 text-sm">{{ store.loading_message }}</div>
		</div>
	</footer>
</template>

<style>
button[disabled] {
	cursor: not-allowed;
}

.loader {
	width: 48px;
	height: 48px;
	border: 5px solid #fff;
	/* border-bottom-color: #ff3d00; */
	border-radius: 50%;
	display: inline-block;
	box-sizing: border-box;
	animation: rotation 1s linear infinite;
	@apply border-b-accent;
}

@keyframes rotation {
	0% {
		transform: rotate(0deg);
	}

	100% {
		transform: rotate(360deg);
	}
}
</style>
